import {
	BoundingBox,
	SnappingRegions,
	BoxCoordinates,
	XYCoordinates,
	Vertex,
	BetweenParams,
	XYOrTopLeft,
} from "./types";
import DockableBox from "./dockableBox";

const clipRightToLeft = (clip: DockableBox, rectA: DockableBox, rectB: DockableBox) => {
	clip.right = rectB.left;
	if (rectB.top > rectA.bottom) {
		clip.bottom = rectB.top;
	}
};
const clipLeftToLeft = (clip: DockableBox, rectA: DockableBox, rectB: DockableBox) => {
	clip.left = rectB.left;
	if (rectB.top < rectA.bottom) {
		clip.bottom = rectB.top;
	}
};
const clipLeftToRight = (clip: DockableBox, rectA: DockableBox, rectB: DockableBox) => {
	clip.left = rectB.right;
	if (rectB.top > rectA.bottom) {
		clip.bottom = rectB.top;
	}
};
const clipRightToRight = (clip: DockableBox, rectA: DockableBox, rectB: DockableBox) => {
	clip.right = rectB.right;
	if (rectB.top < rectA.bottom) {
		clip.bottom = rectB.top;
	}
};

const maybeTopToBottom = (clip: DockableBox, rectA: DockableBox, rectB: DockableBox) => {
	if (rectB.bottom > rectA.top) {
		clip.top = rectB.bottom;
	}
};

const clipRightToLeftMaybeTopToBottom = (clip: DockableBox, rectA: DockableBox, rectB: DockableBox) => {
	clip.right = rectB.left;
	if (rectB.bottom < rectA.top) {
		clip.top = rectB.bottom;
	}
};

const clipLeftToRightMaybeTopToBottom = (clip: DockableBox, rectA: DockableBox, rectB: DockableBox) => {
	clip.left = rectB.right;
	if (rectB.bottom < rectA.top) {
		clip.top = rectB.bottom;
	}
};

class BoxMath {
	static isPointOnSegment(point: XYCoordinates, segment: BoundingBox): boolean {
		// y = mx + b;
		// Equation above transforms into this:
		// (y - y1)	 x - x1
		// -------- = --------
		// y2 - y1 	 x2 - x1

		// which is this:
		// (y - y1) * (x2 - x1) = (x - x1) * ( y2 - y1)
		const { x } = point;
		const { y } = point;
		const x1 = segment.min.x;
		const x2 = segment.max.x;
		const y1 = segment.min.y;
		const y2 = segment.max.y;

		const isInBounds = x >= x1 && x <= x2 && y >= y1 && y <= y2;
		// The equation above will tell us whether the point is on the line, assuming it has no start and end. This checks to see if the point is within the beginning and end of the segment. If not, it can't be on our segment.
		if (!isInBounds) {
			return false;
		}
		return (y - y1) * (x1 - x2) === (x - x1) * (y1 - y2);
	}

	/**
	 * @function {function name}
	 * @param  {type} req {description}
	 * @return {type} {description}
	 */
	static getVertices(req: BoxCoordinates): Vertex[] {
		return [
			// top left
			{
				x: req.left,
				y: req.top,
				label: "topLeft",
			},
			// top right
			{
				x: req.right,
				y: req.top,
				label: "topRight",
			},
			{
				x: req.right,
				y: req.bottom,
				label: "bottomRight",
			},
			{
				x: req.left,
				y: req.bottom,
				label: "bottomLeft",
			},
		];
	}

	/**
	 * @function {function name}
	 * @param  {type} bounds     {description}
	 * @param  {type} bufferSize {description}
	 * @return {type} {description}
	 */
	static getSnappingRegions(bounds: BoxCoordinates, bufferSize: number = 0): SnappingRegions {
		const { left, top, right, bottom } = bounds;
		const leftMinusBuffer = left - bufferSize;
		const leftPlusBuffer = left + bufferSize;
		const topMinusBuffer = top - bufferSize;
		const topPlusBuffer = top + bufferSize;
		const rightMinusBuffer = right - bufferSize;
		const rightPlusBuffer = right + bufferSize;
		const bottomPlusBuffer = bottom + bufferSize;
		const bottomMinusBuffer = bottom - bufferSize;
		return {
			topLeft: {
				min: {
					x: leftMinusBuffer,
					y: topMinusBuffer,
				},
				max: {
					x: leftPlusBuffer,
					y: top,
				},
			},
			topRight: {
				min: {
					x: rightMinusBuffer,
					y: topMinusBuffer,
				},
				max: {
					x: rightPlusBuffer,
					y: top,
				},
			},
			rightTop: {
				min: {
					x: right,
					y: top,
				},
				max: {
					x: rightPlusBuffer,
					y: topPlusBuffer,
				},
			},
			rightBottom: {
				min: {
					x: right,
					y: bottomMinusBuffer,
				},
				max: {
					x: rightPlusBuffer,
					y: bottom,
				},
			},

			bottomLeft: {
				min: {
					x: leftMinusBuffer,
					y: bottom,
				},
				max: {
					x: leftPlusBuffer,
					y: bottomPlusBuffer,
				},
			},
			bottomRight: {
				min: {
					x: rightMinusBuffer,
					y: bottom,
				},
				max: {
					x: rightPlusBuffer,
					y: bottomPlusBuffer,
				},
			},
			leftTop: {
				min: {
					x: leftMinusBuffer,
					y: top,
				},
				max: {
					x: left,
					y: topPlusBuffer,
				},
			},
			leftBottom: {
				min: {
					x: leftMinusBuffer,
					y: bottomMinusBuffer,
				},
				max: {
					x: left,
					y: bottom,
				},
			},
			left: {
				min: {
					x: leftMinusBuffer,
					y: topMinusBuffer,
				},
				max: {
					x: leftPlusBuffer,
					y: bottomPlusBuffer,
				},
			},
			bottom: {
				min: {
					x: leftMinusBuffer,
					y: bottom,
				},
				max: {
					x: rightPlusBuffer,
					y: bottomPlusBuffer,
				},
			},
			right: {
				min: {
					x: rightMinusBuffer,
					y: topPlusBuffer,
				},
				max: {
					x: rightPlusBuffer,
					y: bottomPlusBuffer,
				},
			},
			top: {
				min: {
					x: left,
					y: topMinusBuffer,
				},
				max: {
					x: right,
					y: top,
				},
			},
			inner: {
				min: {
					x: left,
					y: top,
				},
				max: {
					x: right,
					y: bottom,
				},
			},
		};
	}

	/**
	 * @function {function name}
	 * @param  {type} bounds {description}
	 * @return {type} {description}
	 */
	static getWindowBoundingBox(bounds: BoxCoordinates): BoundingBox {
		return {
			min: {
				x: bounds.left,
				y: bounds.top,
			},
			max: {
				x: bounds.right,
				y: bounds.bottom,
			},
		};
	}

	static between(params: BetweenParams) {
		const { min } = params;
		const { max } = params;
		const { num } = params;
		const { inclusive } = params;
		if (inclusive) {
			return num >= min && num <= max;
		}
		return num > min && num < max;
	}

	/**
	 * @function {function name}
	 * @param  {type} window1 {description}
	 * @param  {type} window2 {description}
	 * @return {type} {description}
	 */
	static intersectBoundingBoxes(window1: BoundingBox, window2: BoundingBox): boolean {
		if (window1.max.x < window2.min.x) {
			return false;
		} // 1 is left of 2
		if (window1.min.x > window2.max.x) {
			return false;
		} // 1 is right of 2
		if (window1.max.y < window2.min.y) {
			return false;
		} // 1 is above 2
		return window1.min.y <= window2.max.y;
	}

	/**
	 * @function {function name}
	 * @param  {type} num {description}
	 * @param  {type} pct {description}
	 * @return {type} {description}
	 */
	static getPct(num: number, pct: number): number {
		return pct * num;
	}

	/**
	 * @function {function name}
	 * @param  {type} num {description}
	 * @param  {type} pct {description}
	 * @return {type} {description}
	 */
	static scaleProportionately(num: number, pct: number): number {
		return Math.floor(num + this.getPct(num, pct));
	}

	/**
	 * @function {function name}
	 * @param  {type} num1 {description}
	 * @param  {type} num2 {description}
	 * @return {type} {description}
	 */
	static getPercentChange(num1: number, num2: number): number {
		let pctChange = Math.abs((num1 - num2) / num1);
		if (num2 < num1) {
			pctChange = -pctChange;
		}
		return pctChange;
	}

	/**
	 * Checks if point is in box (inclusive)
	 */
	static isPointInBox(
		point: XYOrTopLeft,
		box: {
			top: number;
			left: number;
			width: number;
			height: number;
			right?: number;
			bottom?: number;
		}
	): boolean {
		const x = "x" in point ? point.x : point.left;
		const y = "y" in point ? point.y : point.top;
		if (!box.right) box.right = box.left + box.width;
		if (!box.bottom) box.bottom = box.top + box.height;
		return x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;
	}

	static cornersInBox(corners: Vertex[], box: DockableBox): string[] {
		return corners.flatMap((corner) => {
			// The goal here is to detect which edges of the clipping box exists within the clipped box's boundaries - EXCLUDING the edges of the clipped box.

			/**
			 * The A = B + C. In other words, it's our hole. B is the window that we're using to fill the hole.
			 *
					In this case, A and B share their top right and top left corners. ALl we care about here is the bottom left and bottom right of B.
					The result of this function is C.
			 		+-------------------+
					|                   |
					|                   |
					|                   |
					|                   |
					|         B         |
					|                   |
					|                   |
					|                   |
					+-------------------+
					|                   |
					|                   |
					|                   |
					|                   |
					|         C         |
					|                   |
					|                   |
					|                   |
					|                   |
					|                   |
					+-------------------+
			 * */

			// The block of code below kicks out any points on B that are corners on A.
			if (this.isPointInBox(corner, box)) {
				const label = corner.label.toLowerCase();
				const bottomRightMatch = label === "bottomright" && corner.y === box.bottom && corner.x === box.right;
				const bottomLeftMatch = label === "bottomleft" && corner.y === box.bottom && corner.x === box.left;
				const topLeftMatch = label === "topleft" && corner.x === box.left && corner.y === box.top;
				const topRightMatch = label === "topright" && corner.x === box.right && corner.y === box.top;
				if (!bottomRightMatch && !bottomLeftMatch && !topLeftMatch && !topRightMatch) {
					return [label];
				}
			}
			return [];
		});
	}

	/**
	 * Going back to our example earlier. Remember, A is the larger box that encompasses B and C. In this case, only two corners are within A. (bottom left, bottom right).
			+-------------------+
			|                   |
			|                   |
			|                   |
			|                   |
			|         B         |
			|                   |
			|                   |
			|                   |
			+-------------------+
			|                   |
			|                   |
			|                   |
			|                   |
			|         C         |
			|                   |
			|                   |
			|                   |
			|                   |
			|                   |
			+-------------------+
		* */
	static clipWithTwoSharedCorners({
		clip,
		cornersInBox,
		box,
	}: {
		clip: DockableBox;
		cornersInBox: string[];
		box: DockableBox;
	}): DockableBox {
		// e.g. left edge is in box.
		if (cornersInBox.includes("topleft") && cornersInBox.includes("bottomleft")) {
			clip.right = box.left;
		} else if (cornersInBox.includes("topright") && cornersInBox.includes("bottomright")) {
			clip.left = box.right;
		} else if (cornersInBox.includes("topleft") && cornersInBox.includes("topright")) {
			clip.bottom = box.top;
		} else if (cornersInBox.includes("bottomleft") && cornersInBox.includes("bottomright")) {
			clip.top = box.bottom;
		}
		return clip;
	}

	/** **
	* In this case, A is the wide horizontal box. B is the tall vertical box. Their bottom left corners align at point X.
	*  In this case, only one corner (bottom left of B) is within the bounds of A.

					B
			+------------------+
			|                  |
			|                  |
			|                  |
			|                  |
			|                  |
			|                  |
			|                  |
			|                  |
			|                  |
			|                  |
			|                  |
			+---------------------------------------------------------------------+
			|                  |                                                  |
			|                  |                                                  |
			|                  |                                                  |
			|                  |                  CLIP                            |  A
			|                  |                                                  |
			|                  |                                                  |
			+------------------+--------------------------------------------------+
			X
		*/
	static clipWithOneSharedCorner({
		clip,
		cornersInBox,
		rectA,
		rectB,
		edge,
	}: {
		clip: DockableBox;
		cornersInBox: string[];
		rectA: DockableBox;
		rectB: DockableBox;
		edge: string;
	}): DockableBox {
		const ISLEFTORRIGHT = edge === "left" || edge === "right";
		const ISTOPORBOTTOM = edge === "top" || edge === "bottom";

		if (cornersInBox.includes("topleft")) {
			ISTOPORBOTTOM ? clipRightToLeft(clip, rectA, rectB) : clipLeftToLeft(clip, rectA, rectB);
		} else if (cornersInBox.includes("topright")) {
			ISTOPORBOTTOM ? clipLeftToRight(clip, rectA, rectB) : clipRightToRight(clip, rectA, rectB);
		} else if (cornersInBox.includes("bottomleft")) {
			ISLEFTORRIGHT ? maybeTopToBottom(clip, rectA, rectB) : clipRightToLeftMaybeTopToBottom(clip, rectA, rectB);
		} else ISLEFTORRIGHT ? maybeTopToBottom(clip, rectA, rectB) : clipLeftToRightMaybeTopToBottom(clip, rectA, rectB);

		return clip;
	}

	/**
	 * Clip A with B. return where the boxes don't intersect.
	 * @param {DockableBox} rectA
	 * @param {DockableBox} rectB
	 *
	 * @memberof BoxMath
	 */
	static clipRect(rectA: DockableBox, rectB: DockableBox, edge: string) {
		let clip = rectA;
		const corners = this.getVertices(rectB);
		// This array will hold the corners of the clipping rectangle that are within the clipped rectangle's boundaries.
		const cornersInBox: string[] = BoxMath.cornersInBox(corners, rectA);

		if (cornersInBox.length === 2) {
			clip = BoxMath.clipWithTwoSharedCorners({ clip, cornersInBox, box: rectB });
		} else {
			clip = BoxMath.clipWithOneSharedCorner({ clip, cornersInBox, rectA, rectB, edge });
		}

		clip.width = clip.right - clip.left;
		clip.height = clip.bottom - clip.top;
		return clip;
	}
}

export default BoxMath;
